import React from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { numberToCurrency } from "helpers/string"
import { Text } from "ui/Typography"
import styles from "./BookingCalendar.module.css"

/**
 * "Select Duration" step for fixed multi-day experiences: one card per booking option the guide
 * created, each showing the day count and the cheapest rate it starts from. Picking one decides
 * the whole trip's price, so it comes before the date.
 */
const DurationSelector = ({ options, selectedKey, onSelect, error, collapsed = false }) => {
  const [t] = useTranslation()

  if (collapsed) {
    const selected = options.find((option) => option.key === selectedKey)
    return (
      <div className={styles.inputContainer}>
        <span className={styles.inputLabel}>{t("booking.multi_day.duration_label")}</span>
        <span className={styles.summaryValue}>
          {selected ? t("booking.multi_day.days", { count: selected.days }) : "—"}
        </span>
      </div>
    )
  }

  return (
    <div className={styles.stepFadeIn}>
      <div className={styles.stepSection}>
        <span className={styles.stepSectionLabel}>{t("booking.multi_day.select_duration")}</span>
        <div className={styles.durationGrid}>
          {options.map((option) => (
            <button
              key={option.key}
              type="button"
              aria-pressed={selectedKey === option.key}
              className={`${styles.durationCard} ${selectedKey === option.key ? styles.durationCardSelected : ""}`}
              onClick={() => onSelect(option)}
            >
              <span className={styles.durationCardDays}>{t("booking.multi_day.days", { count: option.days })}</span>
              {option.fromPrice > 0 && (
                <>
                  <span className={styles.durationCardDivider} aria-hidden="true" />
                  <span className={styles.durationCardPrice}>
                    <span>{t("booking.multi_day.from")}</span>
                    <span>
                      <span className={styles.durationCardAmount}>{numberToCurrency(option.fromPrice)}</span>
                      {t("booking.multi_day.price_unit", {
                        unit: option.isFlatRate ? t("booking.multi_day.per_trip") : t("booking.multi_day.per_adult")
                      })}
                    </span>
                  </span>
                </>
              )}
            </button>
          ))}
        </div>
      </div>
      {error && <Text className={styles.errorText}>{t("booking.multi_day.duration_required")}</Text>}
    </div>
  )
}

DurationSelector.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      days: PropTypes.number.isRequired,
      fromPrice: PropTypes.number,
      isFlatRate: PropTypes.bool
    })
  ).isRequired,
  selectedKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelect: PropTypes.func.isRequired,
  error: PropTypes.bool,
  collapsed: PropTypes.bool
}

export default React.memo(DurationSelector)
